import Button from "components/Button";
import Input from "components/Input";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import CloseIcon from "assets/icons/close.svg?react";
import adminService from "api/adminService";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import InputImitation from "components/InputImitation";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { IAddressBookVerificationAttachment } from "dto/Admin/IAddressBookVerificationAttachment";
import usePDF from "hooks/usePDF";
import { ItemKind } from "dto/IKindItems";
import Spinner from "components/Spinner";
import PDFViewer from "components/PDFViewer";
import AddressBookAttachment from "../AddressBookAttachment";
import { randomString } from "utils/randomString";
import { useTranslation } from "react-i18next";

const VerifyModal: FC<Props> = ({
  addressBookVerificationId,
  toggle,
  reload,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(null);

  const { blob, decryptPackageAndSetBlob, isLoading: isLoadingPdf } = usePDF();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  const [isCompanyExpanded, setIsCompanyExpanded] = useState<boolean>(true);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(false);
  const [isDetails2Expanded, setIsDetails2Expanded] = useState<number>(0);
  const [isAttachmentsExpanded, setIsAttachmentsExpanded] =
    useState<boolean>(false);

  const [selectedAttachment, setSelectedAttachment] =
    useState<IAddressBookVerificationAttachment>(null);

  const [isLoading] = useState(false);
  const { data: verificationData } =
    adminService.useGetAddressBookVerificationDetails(
      addressBookVerificationId
    );

  const { mutate: verify } = adminService.useVerifyAddressBookVerification();

  const handleClickCloseModal = () => toggle(false);

  const [, setShowErrors] = useState({ case: false, files: false });

  const handleError = () => {
    const payload = {
      case: false,
      files: false,
    };

    setShowErrors(payload);
  };

  const handleSave = () => {
    verify(
      {
        ContactFormAddressBookVerificationId: addressBookVerificationId,
        Result: value,
        Approve: value === "standalone",
        ToBeCompleted: value === "partner",
        Reject: value === "reject",
      },
      {
        onSuccess(data, variables, context) {
          toggle(false);
          reload?.();
        },
      }
    );
  };

  useEffect(() => {
    if (!selectedAttachment) return;
    decryptPackageAndSetBlob({
      FileName: selectedAttachment.FileName,
      EncryptionKeyId: null,
      Uri: selectedAttachment.Uri,
      Kind: ItemKind.Email,
    });
  }, [selectedAttachment]);

  useEffect(() => {
    if (verificationData?.data) {
      if (verificationData.data.Attachments?.length) {
        setSelectedAttachment(verificationData.data.Attachments[0]);
      }
    }
  }, [verificationData?.data]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.left}>
        {isLoadingPdf && (
          <div className={styles.loading}>
            <Spinner></Spinner>
          </div>
        )}
        {!isLoadingPdf && blob && (
          <PDFViewer
            className={styles.viewer}
            file={blob}
            sourceFile={blob}
            showHeader={true}
            headerClassName={styles.title}
            title={t("verifyModal.verifyData")}
            onCloseButton={() => toggle(false)}
            withoutBorder={true}
            inModal={true}
            showCloseButton={false}
          />
        )}
      </div>
      <div className={styles.right}>
        <div className={styles.title}>
          <CloseIcon
            onClick={handleClickCloseModal}
            className={styles.closeIcon}
          />
        </div>
        <div className={styles.main}>
          <div className={styles.history}>
            <div className={styles.header}>
              {t("verifyModal.legalPersonData")}
              <div
                onClick={(e) => {
                  setIsCompanyExpanded(!isCompanyExpanded);
                  e.stopPropagation();
                }}
                className={styles.expanderWrapper}
              >
                <FontAwesomeIcon
                  className={classNames(styles.expander)}
                  icon={isCompanyExpanded ? faCaretDown : faCaretUp}
                />
              </div>
            </div>
            {isCompanyExpanded && (
              <div className={styles.inputs}>
                <InputImitation
                  label="Nip"
                  value={verificationData?.data?.Nip}
                />
                <InputImitation
                  label={t("forms.companyName")}
                  value={verificationData?.data?.CompanyName}
                />
                <InputImitation
                  label="REGON"
                  value={verificationData?.data?.Regon}
                />
                <InputImitation
                  label="KRS"
                  value={verificationData?.data?.Krs}
                />
                <InputImitation
                  label={t("verifyModal.companyAddress")}
                  value={verificationData?.data?.Address}
                />
              </div>
            )}
          </div>

          <div className={styles.history}>
            <div className={styles.header}>
              {t("verifyModal.applicant")}{" "}
              {`${verificationData?.data?.FirstName} ${verificationData?.data.LastName}`}
              <div
                onClick={(e) => {
                  setIsDetailsExpanded(!isDetailsExpanded);
                  e.stopPropagation();
                }}
                className={styles.expanderWrapper}
              >
                <FontAwesomeIcon
                  className={classNames(styles.expander)}
                  icon={isDetailsExpanded ? faCaretDown : faCaretUp}
                />
              </div>
            </div>
            {isDetailsExpanded && (
              <div className={styles.inputs}>
                <Input
                  disabled={true}
                  value={verificationData?.data?.FirstName}
                  RightIcon={
                    <div
                      className={classNames(
                        styles.verification,
                        styles.verified
                      )}
                    >
                      {t("identity.verifiedData")}
                    </div>
                  }
                  label={t("forms.firstName")}
                  onChange={(t) => {}}
                ></Input>
                <Input
                  disabled={true}
                  value={verificationData?.data?.LastName}
                  RightIcon={
                    <div
                      className={classNames(
                        styles.verification,
                        styles.verified
                      )}
                    >
                      {t("identity.verifiedData")}
                    </div>
                  }
                  label={t("forms.lastName")}
                  onChange={(t) => {}}
                ></Input>
                <Input
                  disabled={true}
                  value={verificationData?.data?.Pesel}
                  RightIcon={
                    <div
                      className={classNames(
                        styles.verification,
                        styles.verified
                      )}
                    >
                      {t("identity.verifiedData")}
                    </div>
                  }
                  label={"Pesel"}
                  onChange={(t) => {}}
                ></Input>
                <Input
                  disabled={true}
                  value={verificationData?.data?.Email}
                  label={"E-mail"}
                  onChange={(t) => {}}
                ></Input>
              </div>
            )}
          </div>

          {verificationData?.data?.AdditionalVerifications?.map((a, i) => (
            <div className={styles.history}>
              <div className={styles.header}>
                {t("verifyModal.boardMember")} {`${a?.FirstName} ${a.LastName}`}
                <div
                  onClick={(e) => {
                    if ((isDetails2Expanded & Math.pow(2, i + 1)) !== 0) {
                      setIsDetails2Expanded(
                        isDetails2Expanded & ~Math.pow(2, i + 1)
                      );
                    } else {
                      setIsDetails2Expanded(
                        isDetails2Expanded | Math.pow(2, i + 1)
                      );
                    }
                    e.stopPropagation();
                  }}
                  className={styles.expanderWrapper}
                >
                  <FontAwesomeIcon
                    className={classNames(styles.expander)}
                    icon={isDetailsExpanded ? faCaretDown : faCaretUp}
                  />
                </div>
              </div>
              {(isDetails2Expanded & Math.pow(2, i + 1)) !== 0 && (
                <div className={styles.inputs}>
                  <Input
                    disabled={true}
                    value={a.FirstName}
                    RightIcon={
                      <div
                        className={classNames(
                          styles.verification,
                          styles.verified
                        )}
                      >
                        {t("identity.verifiedData")}
                      </div>
                    }
                    label={t("forms.firstName")}
                    onChange={(t) => {}}
                  ></Input>
                  <Input
                    disabled={true}
                    value={a.LastName}
                    RightIcon={
                      <div
                        className={classNames(
                          styles.verification,
                          styles.verified
                        )}
                      >
                        {t("identity.verifiedData")}
                      </div>
                    }
                    label={t("forms.lastName")}
                    onChange={(t) => {}}
                  ></Input>
                  <Input
                    disabled={true}
                    value={a.Pesel}
                    RightIcon={
                      <div
                        className={classNames(
                          styles.verification,
                          styles.verified
                        )}
                      >
                        {t("identity.verifiedData")}
                      </div>
                    }
                    label={"Pesel"}
                    onChange={(t) => {}}
                  ></Input>
                </div>
              )}
            </div>
          ))}

          <div className={styles.history}>
            <div className={styles.header}>
              {t("documents.attachments")}
              <div
                onClick={(e) => {
                  setIsAttachmentsExpanded(!isAttachmentsExpanded);
                  e.stopPropagation();
                }}
                className={styles.expanderWrapper}
              >
                <FontAwesomeIcon
                  className={classNames(styles.expander)}
                  icon={isAttachmentsExpanded ? faCaretDown : faCaretUp}
                />
              </div>
            </div>
            {isAttachmentsExpanded && (
              <div className={styles.list}>
                {verificationData?.data.Attachments.map((attach) => (
                  <AddressBookAttachment
                    className={styles.attachment}
                    key={attach.Id + randomString()}
                    onDownload={(attach) => {}}
                    onPreview={(attach) => {
                      setSelectedAttachment(attach);
                    }}
                    attachment={attach}
                  />
                ))}
              </div>
            )}
          </div>
          <div className={styles.spacer}></div>

          <FormControl>
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={value}
              onChange={handleChange}
            >
              <FormControlLabel
                value="standalone"
                control={<Radio />}
                label={t("verifyModal.approveAndCreate")}
              />
              <FormControlLabel
                value="partner"
                control={<Radio />}
                label={t("verifyModal.partialRepresentation")}
              />
              <FormControlLabel
                value="reject"
                control={<Radio />}
                label={t("verifyModal.noRepresentation")}
              />
            </RadioGroup>
          </FormControl>
        </div>

        <div className={styles.footer}>
          <Button
            className={styles.button}
            onClick={handleSave}
            text={t("common.approve")}
            loading={isLoading}
            handleError={handleError}
            disabled={!value}
          />
        </div>
      </div>
    </div>
  );
};

export default VerifyModal;
